import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaUser,
  FaHeart,
  FaSignOutAlt,
  FaStore,
  FaCoffee,
  FaClock,
  FaIceCream,
  FaGlassCheers,
  FaDumbbell,
  FaShoppingBag,
  FaCut,
  FaSpa,
  FaPaintBrush,
  FaUtensils,
  FaHamburger,
  FaShoppingCart,
  FaTh,
  FaArrowLeft,
  FaTicketAlt,
} from "react-icons/fa";
import blockClubLogo from "../../media/BlockClub_rectangular.png";
import LanguageSwitcher from "../LanguageSwitcher";
import SearchDropdown from "../SearchDropdown/SearchDropdown";
import { useAuth } from "../../contexts/AuthContext";
import { appendCompanyCode } from "../../hooks/useCompanyCode";
import "./AppHeader.css";

// Icon mapping for dynamic rendering
const ICON_MAP = {
  FaCoffee,
  FaClock,
  FaWatch: FaClock, // Map FaWatch to FaClock for backward compatibility
  FaIceCream,
  FaGlassCheers,
  FaDumbbell,
  FaShoppingBag,
  FaCut,
  FaSpa,
  FaPaintBrush,
  FaUtensils,
  FaStore,
  FaHamburger,
  FaShoppingCart,
};

// Helper to get icon component by name
const getIconComponent = (iconName) => {
  return ICON_MAP[iconName] || FaStore; // Default to FaStore if icon not found
};

/**
 * Shared navigation header component used across the perks app
 * Displays Block Club logo, search (desktop), and user actions
 */
export default function AppHeader({
  viewMode = "public",
  wishlistCount = 0,
  onLogin,
  onLogout,
  onWishlistClick,
  onProfileClick,
  showActions = true,
  showSearch = false,
  showUtilityBar = true,
  userLocation = null,
  categories = [],
  selectedCategory = "All",
  onCategoryChange = null,
  categoryIconMap = {},
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { email, customerToken } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Refs for category scrolling
  const categoriesRowRef = useRef(null);
  const categoryRefs = useRef({});

  // Check if we're on the home page (including with company code)
  const isHomePage = location.pathname === "/" ||
                     location.pathname === "/perks-directory" ||
                     location.pathname.startsWith("/perks-directory/");

  // Scroll selected category to center on mobile
  const scrollCategoryToCenter = (category) => {
    // Only scroll on mobile
    if (window.innerWidth > 768) return;

    const container = categoriesRowRef.current;
    const button = categoryRefs.current[category];

    if (!container || !button) return;

    // Calculate the position to center the button
    const containerWidth = container.offsetWidth;
    const buttonLeft = button.offsetLeft;
    const buttonWidth = button.offsetWidth;

    // Calculate scroll position to center the button
    const scrollPosition = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);

    // Check user's motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Smooth scroll to center (or instant if user prefers reduced motion)
    container.scrollTo({
      left: scrollPosition,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  };

  // Handle category selection - navigate to home if not there
  const handleCategoryClick = (category) => {
    // Scroll category to center on mobile
    scrollCategoryToCenter(category);

    // If not on home page, navigate to home with selected category in state
    if (!isHomePage) {
      navigate(appendCompanyCode("/perks-directory"), { state: { selectedCategory: category } });
    } else {
      // If already on home page, just update the category
      if (onCategoryChange) {
        onCategoryChange(category);
      }
    }
  };

  // Scroll the selected category to center when it changes
  useEffect(() => {
    if (selectedCategory) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollCategoryToCenter(selectedCategory);
      }, 100);
    }
  }, [selectedCategory]);

  // Scroll detection for mobile and desktop header behavior
  useEffect(() => {
    const handleScroll = () => {
      const width = window.innerWidth;
      const scrollY = window.scrollY;

      // Mobile behavior (max-width: 768px)
      if (width <= 768) {
        const shouldBeScrolled = scrollY > 50;
        setIsScrolled(shouldBeScrolled);
        setHideHeader(false); // Always show on mobile
      } else {
        // Desktop behavior - hide header by default, only show at very top
        setIsScrolled(false);

        // Show header only when at the very top (within 50px)
        if (scrollY <= 50) {
          setHideHeader(false);
        } else {
          setHideHeader(true);
        }

        setLastScrollY(scrollY);
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    // Check initial state
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [lastScrollY]);

  return (
    <>
      {/* Utility bar - Desktop only, shown only on loyalty directory */}
      {showUtilityBar && (
        <>
          <div className={`app-header-utility-bar ${hideHeader ? 'hidden' : ''}`}>
            <div className="app-header-utility-content">
              <div className="app-header-utility-right">
                <button
                  onClick={() => navigate("/become-partner")}
                  className="app-header-utility-link app-header-utility-button"
                >
                  <FaStore />
                  {t('header.becomePartner', 'Become a Partner')}
                </button>
                <LanguageSwitcher />
              </div>
            </div>
          </div>

          {/* Thin line above header */}
          <div className={`app-header-top-line ${hideHeader ? 'hidden' : ''}`} />
        </>
      )}

      <nav className={`app-header ${showUtilityBar ? 'with-utility-bar' : 'without-utility-bar'} ${isScrolled ? 'scrolled' : ''} ${hideHeader ? 'hidden' : ''}`}>
        <div className="app-header-top-row">
          <button
            className="app-header-logo"
            onClick={() => { window.location.href = 'https://www.blockclubevents.com'; }}
            aria-label={t('header.logoAria')}
          >
            <img src={blockClubLogo} alt="Block Club logo" />
          </button>

          {/* Search Bar with Mobile Back Button - Mobile & Desktop */}
          {showSearch && (
            <div className="app-header-search-container">
              {/* Mobile Back Button - Always rendered but hidden on home page to prevent layout shift */}
              <button
                className={`app-header-mobile-back ${isHomePage ? 'hidden-on-home' : ''}`}
                onClick={() => {
                  const eventSubpageMatch = location.pathname.match(/^\/events\/([^/]+)\/(tickets|auction|vote)$/);
                  const isEventDetail = /^\/events\/[^/]+$/.test(location.pathname);
                  if (eventSubpageMatch) {
                    navigate(`/events/${eventSubpageMatch[1]}`);
                  } else if (isEventDetail) {
                    window.location.href = 'https://www.blockclubevents.com';
                  } else if (window.history.state?.idx > 0) {
                    navigate(-1);
                  } else {
                    window.location.href = 'https://www.blockclubevents.com';
                  }
                }}
                aria-label="Go back"
                disabled={isHomePage}
              >
                <FaArrowLeft />
              </button>
              <SearchDropdown
                userLocation={userLocation}
                viewMode={viewMode}
                email={email}
                customerToken={customerToken}
              />
            </div>
          )}

          {showActions && (
            <div className={`app-header-actions ${viewMode === "public" ? "public-view" : "authenticated-view"}`}>
            {viewMode === "public" ? (
              <>
                <button
                  onClick={onLogin}
                  className="btn-secondary"
                  title={t('header.signInTitle')}
                >
                  <FaUser />
                  <span className="btn-text">{t('header.joinNow')}</span>
                </button>
              </>
            ) : (
              <>
                {/* Wishlist Button - Only visible when logged in */}
                {onWishlistClick && (
                  <button
                    className="btn-secondary wishlist-btn"
                    onClick={onWishlistClick}
                    title={t('header.savedTitle')}
                  >
                    <FaHeart />
                    <span className="btn-text">{t('header.saved')}</span>
                    {wishlistCount > 0 && (
                      <span className="wishlist-badge">{wishlistCount}</span>
                    )}
                  </button>
                )}

                {/* Profile Button */}
                {onProfileClick && (
                  <button
                    className="btn-secondary"
                    onClick={onProfileClick}
                    title={t('header.profileTitle')}
                  >
                    <FaUser />
                    <span className="btn-text">{t('header.profile')}</span>
                  </button>
                )}

                {/* Logout Button */}
                <button
                  className="btn-secondary"
                  onClick={onLogout}
                  title={t('header.logout')}
                >
                  <FaSignOutAlt />
                  <span className="btn-text">{t('header.logout')}</span>
                </button>
              </>
            )}
            </div>
          )}
        </div>

        {/* Category Filter Row - Mobile & Desktop */}
        {/* {showSearch && categories.length > 0 && (
          <div className="app-header-categories-row" ref={categoriesRowRef}>
            {categories.map((category) => (
              <button
                key={category}
                ref={(el) => {
                  if (el) {
                    categoryRefs.current[category] = el;
                  }
                }}
                className={`app-header-category-chip ${
                  isHomePage && selectedCategory === category ? "active" : ""
                }`}
                onClick={() => handleCategoryClick(category)}
              >
                {(() => {
                  // Use FaTh icon for "All", FaCalendarAlt for "Events", otherwise use icon from map with fallback to FaStore
                  const IconComponent = category === "All"
                    ? FaTh
                    : category === "Events"
                    ? FaTicketAlt
                    : (categoryIconMap[category] ? getIconComponent(categoryIconMap[category]) : FaStore);

                  return (
                    <span className="category-icon">
                      <IconComponent />
                    </span>
                  );
                })()}
                <span>{t(`categories.${category}`, category)}</span>
              </button>
            ))}
          </div>
        )} */}
      </nav>
    </>
  );
}
